<template>
  <div>

    <div v-if="error" class="error" v-html="error"></div>

    <div v-if="sponsor">
      <div class="lead-in">
        <div class="wrapper">
          <div class="lead-in--image">
            <img v-if="sponsor.acf_json.member_image" a :src="sponsor.acf_json.member_image.sizes.large" :width="sponsor.acf_json.member_image.sizes['large-width']" :height="sponsor.acf_json.member_image.sizes['large-height']" />
            <router-link :to="editLink" class="image-placeholder edit-link" v-else-if="canEdit">
              <span>
                <FontAwesomeIcon :icon="icon.edit" />
                Upload an Image
              </span>
            </router-link>
          </div>

          <div class="lead-in--content">
            <router-link v-if="canEdit" :to="editLink" class="edit-link edit-link--main">
              <FontAwesomeIcon :icon="icon.edit" />
              Edit Your Profile
            </router-link>

            <div class="company-logo" v-if="sponsor.acf.company && sponsor.acf_json.company_logo">
              <img v-if="sponsor.acf_json.company_logo" a :src="sponsor.acf_json.company_logo.logo" />
            </div>

            <h1>{{ sponsor.name }}</h1>

            <!-- Company -->
            <dl v-if="sponsor.acf.company">
              <dt>Company:</dt>
              <dd>{{ sponsor.acf.company }} <a :href="sponsor.acf.company_url" target="_blank" v-if="sponsor.acf.company_url">{{ cleanURL(sponsor.acf.company_url) }}</a></dd>
            </dl>
            <dl v-else-if="canEdit">
              <dt>Company:</dt>
              <dd><router-link :to="editLink" class="edit-link">
                <FontAwesomeIcon :icon="icon.edit" />
                Add Company
              </router-link></dd>
            </dl>

            <!-- Phone -->
            <dl v-if="sponsor.acf.telephone">
              <dt>Phone:</dt>
              <dd><a :href="`tel:+1${sponsor.acf.telephone.replace('ext', ',').replaceAll(/[^0-9,]/g, '')}`">{{ sponsor.acf.telephone }}</a></dd>
            </dl>
            <dl v-else-if="canEdit">
              <dt>Phone:</dt>
              <dd><router-link :to="editLink" class="edit-link">
                <FontAwesomeIcon :icon="icon.edit" />
                Add Telephone
              </router-link></dd>
            </dl>

            <!-- Email -->
            <dl v-if="sponsor.meta.email">
              <dt>Email:</dt>
              <dd><a class="email-address" :href="`mailto:${sponsor.meta.email}`">{{ sponsor.meta.email }}</a></dd>
            </dl>

            <!-- Social -->
            <dl v-if="social_profiles.length > 0" class="social">
              <dt>Connect on Social:</dt>
              <dd>
                <span v-for="social, key in social_profiles" v-bind:key="key">
                  <a :href="social.profile_url" target="_blank" v-if="social.profile_url" :title="getSocialMedia(social.social_media).name">
                    <FontAwesomeIcon :icon="getSocialMedia(social.social_media).icon" :aria-label=" social.social_media" />
                  </a>
                </span>
              </dd>
            </dl>
            <dl v-else-if="canEdit" class="social">
              <dt>Connect on Social:</dt>
              <dd><router-link :to="editLink" class="edit-link">
                <FontAwesomeIcon :icon="icon.edit" />
                Add Social Profiles
              </router-link></dd>
            </dl>

          </div>
        </div>
      </div>
      <div class="body">
        <div class="wrapper" v-if="sponsor.acf.business_information">
          <h2 class="body--heading">Business Information</h2>
          <div class="body--content">

            <div v-if="sponsor.acf.business_information" v-html="autop(sponsor.acf.business_information)"></div>

            <div v-else-if="canEdit">
              <router-link :to="editLink" class="edit-link">
                <FontAwesomeIcon :icon="icon.edit" />
                Add Business Information
              </router-link>
            </div>
          </div>
        </div>
        <div class="wrapper" v-if="sponsor.acf.forum_speaking_topics">
          <h2 class="body--heading">Forum Speaking Topics</h2>
          <div class="body--content">

            <div v-if="sponsor.acf.forum_speaking_topics" v-html="autoli(sponsor.acf.forum_speaking_topics, 'ol', 5)"></div>

            <div v-else-if="canEdit">
              <router-link :to="editLink" class="edit-link">
                <FontAwesomeIcon :icon="icon.edit" />
                Add Forum Speaking Topics
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'

export default {
  name: 'SponsorProfile',
  components: {
    FontAwesomeIcon
  },
  mounted() {
    this.$store.dispatch('getSponsor', this.$route.params.slug).then(() => {
      this.sponsor = this.$store.getters.getSponsor(this.$route.params.slug)

      if (!this.sponsor) {
        this.$router.push('/sponsors')
      } else if(this.sponsor.meta.role == 'member') {
        this.$router.push('/members/' + this.$route.params.slug)
      }
    })
  },
  computed: {
    error() {
      return this.$store.getters.getError
    },
    canEdit() {
      const user = this.$store.getters.getUser
      return user && (user.roles.includes('administrator') || user.id === this.sponsor.id)
    },
    social_profiles() {
      return [this.sponsor.acf.social_1, this.sponsor.acf.social_2, this.sponsor.acf.social_3].filter(social => social.profile_url !== '' && social.social_media !== '')
    },
    social_media() {
      return this.$store.getters.getSocialMedia
    },
    editLink() {
      return `/sponsors/${this.sponsor.slug}/edit`
    },
    myBusinesses() {
      if (!this.sponsor.acf.businesses) return []
      return this.sponsor.acf.businesses.filter(business => business.business_name !== '').sort((a, b) => a.year - b.year);
    }
  },
  watch: {
    $route(to) {
      this.$store.dispatch('getSponsor', to.params.slug).then(() => {
        this.sponsor = this.$store.getters.getSponsor(to.params.slug)

        if (!this.sponsor) {
          this.$router.push('/sponsors')
        }
      })
    }
  },
  data() {
    return {
      sponsor: false,
      icon: {
        edit: faPenToSquare
      }
    }
  },
  methods: {
    cleanURL(url) {
      url = url.replace(/(^\w+:|^)\/\//, '');
      url = url.replace(/^www\./, '');
      url = url.replace(/\/$/, '');
      return url;
    },
    getSocialMedia(val) {
      return this.social_media.find(social => social.value === val)
    },
    autop(content) {
      content = content.replace(/(?:\r\n|\r|\n)/g, '<br />')
      content = '<p>' + content.replace(/<br \/><br \/>/g, '</p><p>') + '</p>'
      return content
    },
    autoli(content, type = 'ul', max) {
      content = content.replace(/(?:\r\n|\r|\n)/g, '<br />')
      if (max) {
        content = content.split('<br />').slice(0, max).join('<br />')
      }
      content = '<'+type+'><li>' + content.replace(/<br \/>/g, '</li><li>') + '</li></'+type+'>'
      return content
    }
  },
  metaInfo() {
    return {
      title: this.sponsor ? this.sponsor.name : 'Sponsor Profile'
    }
  }
}
</script>
